import { useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Box, Checkbox } from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import { BaseDialog, ConfirmationDialog, LoadingFeedback } from 'components'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'

import { useFetch, usePermissions, useSnackbar } from 'hooks'
import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'

const ServiceCompaniesDialog = ({
  open,
  setOpen,
  paymentContractId,
  paymentService,
}) => {
  const snackbar = useSnackbar()
  const { permitted } = usePermissions()
  const [loading, setLoading] = useState(false)
  const [companyToUnselect, setCompanyToUnselect] = useState(null)

  const { response: preRegistrationsResponse, error: preRegistrationsError } =
    useFetch(
      service.dponet.paymentContract.preRegistrations,
      { paymentContractId, params: { perPage: 1000 } },
      [],
      open,
    )

  const { response, error, refresh } = useFetch(
    service.dponet.paymentContractServices.getCompanies,
    { paymentContractId, paymentServiceId: paymentService?.id },
    [],
    open,
  )

  const contractCompanies = (
    preRegistrationsResponse?.data?.preRegistrations || []
  ).filter((preRegistration) => !!preRegistration?.companyId)
  const selectedCompanyIds = (
    response?.data?.paymentServiceCompanies || []
  ).map((paymentServiceCompany) => paymentServiceCompany?.company?.id)
  const isFirstLoading = !response || !preRegistrationsResponse
  const hasListError =
    (!!error && !response) ||
    (!!preRegistrationsError && !preRegistrationsResponse)
  const isInactiveService = !paymentService?.active

  const canSelect = permitted(
    constants.permissions.PAYMENT_CONTRACTS.SELECT_SERVICE_COMPANY,
  )
  const canUnselect = permitted(
    constants.permissions.PAYMENT_CONTRACTS.UNSELECT_SERVICE_COMPANY,
  )

  const handleSelectCompany = async (companyId) => {
    setLoading(true)
    try {
      await service.dponet.paymentContractServices.selectCompany({
        paymentContractId,
        paymentServiceId: paymentService?.id,
        companyId,
      })
      snackbar.open({
        message: 'Empresa marcada no serviço de Revisão com sucesso!',
        variant: 'success',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Falha ao marcar a empresa no serviço de Revisão.',
        variant: 'error',
      })
      refresh()
    } finally {
      setLoading(false)
    }
  }

  const handleUnselectCompany = async () => {
    setLoading(true)
    try {
      await service.dponet.paymentContractServices.unselectCompany({
        paymentContractId,
        paymentServiceId: paymentService?.id,
        companyId: companyToUnselect?.companyId,
      })
      snackbar.open({
        message: 'Empresa desmarcada do serviço de Revisão com sucesso!',
        variant: 'success',
      })
      refresh()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Falha ao desmarcar a empresa do serviço de Revisão.',
        variant: 'error',
      })
      refresh()
    } finally {
      setLoading(false)
      setCompanyToUnselect(null)
    }
  }

  const handleChangeCompany = (contractCompany, isSelected) => {
    if (isSelected) return setCompanyToUnselect(contractCompany)

    handleSelectCompany(contractCompany?.companyId)
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title="Empresas que usam o serviço de Revisão"
      closeButtonText="Fechar"
      dialogSize="md"
    >
      <LoadingFeedback open={loading} />
      {isInactiveService && (
        <Box mb={2}>
          <Alert severity="info">
            Este serviço está inativo. A marcação fica salva, mas a Revisão das
            empresas só muda quando o serviço for reativado.
          </Alert>
        </Box>
      )}
      {!!response &&
        !isEmpty(contractCompanies) &&
        isEmpty(selectedCompanyIds) && (
          <Box mb={2}>
            <Alert severity="warning">
              Nenhuma empresa está na Revisão deste serviço. Marque as empresas
              do contrato que usam o serviço.
            </Alert>
          </Box>
        )}
      {hasListError ? (
        <Alert severity="error">
          Não foi possível carregar as empresas do contrato. Feche e abra
          novamente para tentar outra vez.
        </Alert>
      ) : (
        <Table
          emptyMessage="Nenhuma empresa encontrada no contrato"
          isLoading={isFirstLoading}
          noWrap
        >
          <TableHead>
            <TableRow>
              <TableCell width="10%" align="left">
                Usa o serviço
              </TableCell>
              <TableCell width="60%" align="left">
                Empresa
              </TableCell>
              <TableCell width="30%" align="left">
                Documento
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contractCompanies.map((contractCompany) => {
              const isSelected = selectedCompanyIds.includes(
                contractCompany?.companyId,
              )

              return (
                <TableRow key={contractCompany?.companyId}>
                  <TableCell disableTooltip align="left">
                    <Checkbox
                      color="primary"
                      checked={isSelected}
                      disabled={
                        loading || (isSelected ? !canUnselect : !canSelect)
                      }
                      onChange={() =>
                        handleChangeCompany(contractCompany, isSelected)
                      }
                      inputProps={{
                        'aria-label': `Usa o serviço: ${contractCompany?.name}`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="left">{contractCompany?.name}</TableCell>
                  <TableCell align="left">
                    {helpers.formatters.document.identify(
                      contractCompany?.document,
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
      {!!companyToUnselect && (
        <ConfirmationDialog
          open={!!companyToUnselect}
          setOpen={() => setCompanyToUnselect(null)}
          message={`Desmarcar a empresa ${companyToUnselect?.name} do serviço de Revisão?`}
          alert={
            isInactiveService
              ? 'O serviço está inativo: a empresa não volta para a Revisão quando ele for reativado.'
              : [
                  'As Revisões abertas desta empresa serão encerradas.',
                  'O histórico das Revisões continua disponível para consulta, sem ações.',
                ]
          }
          alertColor="warning"
          cancelButtonText="Cancelar"
          buttonText="Desmarcar"
          actionAcceptButton={handleUnselectCompany}
        />
      )}
    </BaseDialog>
  )
}

ServiceCompaniesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  paymentContractId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  paymentService: PropTypes.object,
}

export default ServiceCompaniesDialog
